'use client';

import { useMemo } from 'react';
import {
  Zap,
  CheckCircle,
  Clock,
  ArrowRight,
} from 'lucide-react';
import type { PrioritizedFinding } from '@/lib/audit/report-organizer';
import { getEffortLabel } from '@/lib/audit/report-organizer';
import { CriterionTag } from './CriterionTag';
import styles from './QuickWins.module.css';

interface QuickWinsProps {
  findings: PrioritizedFinding[];
  locale?: string;
  onFindingClick?: (finding: PrioritizedFinding) => void;
}

/**
 * Quick Wins Component
 * Displays high impact + low effort findings
 * Compact cards with horizontal border pattern per design system
 */
export function QuickWins({ findings, locale = 'en', onFindingClick }: QuickWinsProps) {
  const i18n = useMemo(
    () => ({
      en: {
        title: 'Quick Wins',
        subtitle: 'High Impact, Low Effort',
        effortLabel: 'Effort',
        fix: 'Fix',
        noQuickWins: 'No quick wins identified',
        description: 'These issues can be fixed quickly and will have significant impact.',
      },
      es: {
        title: 'Victorias Rápidas',
        subtitle: 'Alto Impacto, Bajo Esfuerzo',
        effortLabel: 'Esfuerzo',
        fix: 'Arreglar',
        noQuickWins: 'No se identificaron victorias rápidas',
        description: 'Estos problemas se pueden arreglar rápidamente y tendrán un impacto significativo.',
      },
    }),
    []
  );

  const t = i18n[locale as 'en' | 'es'] || i18n.en;

  if (!findings || findings.length === 0) {
    return null; // Don't show section if no quick wins
  }

  return (
    <section className={styles.container}>
      {/* Section Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <Zap size={20} />
        </div>
        <div className={styles.headerText}>
          <span className={styles.sectionLabel}>{t.title}</span>
          <h2 className={styles.title}>{t.subtitle}</h2>
        </div>
        <span className={styles.count}>{findings.length}</span>
      </div>

      <p className={styles.description}>{t.description}</p>

      {/* Quick Wins List */}
      <div className={styles.list}>
        {findings.map((finding, index) => (
          <div
            key={finding.principleId || index}
            className={styles.item}
            onClick={() => onFindingClick?.(finding)}
            role={onFindingClick ? 'button' : undefined}
            tabIndex={onFindingClick ? 0 : undefined}
          >
            {/* Check Icon */}
            <div className={styles.checkIcon}>
              <CheckCircle size={18} />
            </div>

            {/* Content */}
            <div className={styles.content}>
              <h3 className={styles.findingTitle}>
                {finding.principleTitle || 'UX Issue'}
              </h3>
              <p className={styles.findingDescription}>
                {truncateText(finding.recommendation || finding.description, 100)}
              </p>
            </div>

            {/* Effort Badge */}
            <div className={styles.meta}>
              <CriterionTag code={finding.auditCode} categoryLabel={finding.auditCategoryLabel} />
              <span className={styles.effortBadge}>
                <Clock size={12} />
                {getEffortLabel(finding.effortEstimate)}
              </span>
              {onFindingClick && (
                <ArrowRight size={16} className={styles.arrow} />
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

export default QuickWins;
